/**
 * [PURPOSE]: Styled replacement for the Split Stack window.prompt() in
 *   ContextMenuComponent — lets the player pick how many of a stackable
 *   item to peel into a new stack via a bounded +/- stepper instead of a
 *   raw browser prompt (which breaks dark theme and can't be clamped
 *   in-UI — a bad paste could only be caught after the fact).
 * [WHY]: board item UI-CONFIRM-UNIFICATION — window.prompt is synchronous
 *   and unstyled; every other numeric-allocation surface in this codebase
 *   (SlotRecoveryModal) already uses a stepper on the MobileModal scaffold.
 * [INTEGRATION]: Mounted by ContextMenuComponent, which owns `splitTarget`
 *   state (the item being split) so the dialog survives the context menu
 *   closing — the menu and the dialog are independent overlays.
 * [BEHAVIOR]: count is clamped 1..(quantity-1); default is half the stack
 *   rounded down (floor(quantity/2)), matching the prior window.prompt
 *   default. Confirm calls onConfirm(count); Cancel/Escape/backdrop calls
 *   onCancel — MobileModal owns Escape + body-scroll-lock internally.
 */

import React, { useState, useEffect } from 'react';
import MobileModal from './MobileModal';

const SplitStackDialog = ({ isOpen, item, onConfirm, onCancel }) => {
  const quantity = item?.quantity || 1;
  const max = Math.max(1, quantity - 1);
  const defaultCount = Math.min(max, Math.max(1, Math.floor(quantity / 2)));
  const [count, setCount] = useState(defaultCount);

  // Reset to the default split count whenever the dialog opens (or targets
  // a different item) — mirrors SlotRecoveryModal's reset-on-open pattern.
  useEffect(() => {
    if (isOpen) setCount(defaultCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, item?.id]);

  const handleIncrement = () => setCount(c => Math.min(max, c + 1));
  const handleDecrement = () => setCount(c => Math.max(1, c - 1));

  return (
    <MobileModal
      isOpen={isOpen}
      onClose={onCancel}
      title="Split Stack"
      showSaveButton={false}
      overlayClassName="z-[60]"
      ariaLabel="Split Stack"
    >
      <p className="text-sm text-gray-300 mb-6">
        Split how many <span className="font-semibold text-amber-300">{item?.name}</span> into a new stack? (1-{max})
      </p>

      <div className="flex items-center justify-center gap-4 mb-6">
        <button
          type="button"
          aria-label="Decrease split count"
          onClick={handleDecrement}
          disabled={count <= 1}
          className="w-11 h-11 rounded bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600 text-white text-lg font-bold disabled:cursor-not-allowed"
        >
          −
        </button>
        <span
          data-testid="split-count-display"
          aria-live="polite"
          className="text-white text-2xl font-mono w-12 text-center"
        >
          {count}
        </span>
        <button
          type="button"
          aria-label="Increase split count"
          onClick={handleIncrement}
          disabled={count >= max}
          className="w-11 h-11 rounded bg-purple-700 hover:bg-purple-600 disabled:bg-gray-800 disabled:text-gray-600 text-white text-lg font-bold disabled:cursor-not-allowed"
        >
          +
        </button>
      </div>

      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded transition-colors min-h-[44px] focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onConfirm(count)}
          className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded transition-colors min-h-[44px] focus:outline-none focus:ring-2 focus:ring-amber-400"
        >
          Split
        </button>
      </div>
    </MobileModal>
  );
};

export default SplitStackDialog;
